import React from 'react';
import DynamicFormWidget, { IDynamicFormLayout } from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import { DataService } from 'src/services/data-service';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { Client } from 'src/types/client.types';

interface ClientDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  updateData?: Client;
}

const ClientDrawer: React.FC<ClientDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  updateData
}) => {
  const isEdit = !!updateData;
  const dispatch = useAppDispatch();

  const formConfig: Record<string, DynamicFieldTypes> = {
    name: {
      type: DynamicField.TEXT,
      required: true,
      name: 'name',
      title: 'Client Name',
      value: updateData?.Name || '',
      disabled: false,
      placeholder: 'Enter client name',
      minChars: 2,
      maxChars: 100
    },
    email: {
      type: DynamicField.EMAIL,
      required: true,
      name: 'email',
      title: 'Email Address',
      value: updateData?.Email || '',
      disabled: false,
      placeholder: 'user@example.com'
    },
    password: {
      type: DynamicField.TEXT,
      required: !isEdit,
      name: 'password',
      title: 'Password',
      value: '',
      disabled: false,
      inputType: 'password',
      hidden: isEdit,
      minChars: 8,
      placeholder: 'Minimum 8 characters',
      customValidation: (value: any) => {
        if (!isEdit && value) {
          // Check for at least one uppercase, one lowercase, one number, and one special character
          const hasUpperCase = /[A-Z]/.test(value);
          const hasLowerCase = /[a-z]/.test(value);
          const hasNumber = /[0-9]/.test(value);
          const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

          if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
            return 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
          }
        }
        return '';
      }
    },
    confirmPassword: {
      type: DynamicField.TEXT,
      required: !isEdit,
      name: 'confirmPassword',
      title: 'Confirm Password',
      value: '',
      disabled: false,
      inputType: 'password',
      hidden: isEdit,
      placeholder: 'Re-enter password',
      customValidation: (value: any, allValues: Record<string, any>) => {
        if (!isEdit && value !== allValues.password) {
          return 'Passwords do not match';
        }
        return '';
      }
    }
  };

  // Define the layout with groups
  const formLayout: IDynamicFormLayout = {
    groups: [
      {
        title: 'Client Information',
        fields: ['name', 'email'],
        columns: 1
      },
      {
        title: 'Security',
        fields: ['password', 'confirmPassword'],
        columns: isEdit ? 1 : 2
      }
    ]
  };

  const submit = async (data: Record<string, any>) => {
    try {
      // Prepare the payload according to API requirements
      const payload: any = {
        name: data.name,
        email: data.email
      };

      // Only include password for new clients
      if (!isEdit) {
        payload.password = data.password;
      }

      if (isEdit && updateData) {
        const req = await DataService.put(`api/Client/update/${updateData.Id}`, payload);
        const resJson = await req.json();

        if (req.ok && resJson.success) {
          dispatch(showAlert({ message: 'Client updated successfully!', type: 'success' }));
          onSuccess();
          onClose();
          return true;
        } else {
          dispatch(showAlert({ message: resJson.message || 'Failed to update client', type: 'error' }));
          return false;
        }
      } else {
        const req = await DataService.post('api/Client/create', payload);
        const resJson = await req.json();

        if (req.ok && resJson.success) {
          dispatch(showAlert({ message: 'Client created successfully!', type: 'success' }));
          onSuccess();
          onClose();
          return true;
        } else {
          dispatch(showAlert({ message: resJson.message || 'Failed to create client', type: 'error' }));
          return false;
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
      dispatch(showAlert({ message: 'Network error. Please check your connection and try again.', type: 'error' }));
      return false;
    }
  };

  return (
    <GenericDialog
      title={isEdit ? 'Edit Client' : 'Add New Client'}
      open={open}
      onClose={onClose}
    >
      <DynamicFormWidget
        title=""
        drawerMode
        fields={formConfig}
        onSubmit={submit}
        submitButtonText={isEdit ? 'Update Client' : 'Create Client'}
        showCancelButton
        onCancel={onClose}
        initialValues={
          updateData
            ? {
                name: updateData.Name,
                email: updateData.Email
              }
            : {}
        }
        enableEnterSubmit
        autoFocusFirst
        layout={formLayout}
        validationMode="onChange"
      />
    </GenericDialog>
  );
};

export default ClientDrawer;
